<template>
    <div>
        <div v-if="hasError">
            <ul>
                <li v-for="(error, index) in errorMessages" :key="index">
                    <error :error="error" />
                </li>
            </ul>
        </div>

        <div class="d-flex justify-end mb-4">
            <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateModal">
                Studiengang anlegen
            </v-btn>
        </div>

        <v-data-table-server
            :loading="studiengangStore.isLoading || fachbereichStore.isLoading"
            :headers="headers"
            data-test="studiengang-table"
            :items="studiengaenge"
            class="mb-0"
            density="compact"
            multi-sort
            hide-default-footer
            items-length=""
            :items-per-page="itemsPerPage"
            :items-per-page-options="itemsPerPageOptions"
            :items-per-page-text="itemsPerPageText"
            :no-data-text="noItemsText"
            :loading-text="loadingText"
            mobile-breakpoint="md"
        >
            <template #item.abschlussArt="{ item }">
                {{ item.abschlussArt.abschluss }}
            </template>

            <template #item.name="{ item }">
                {{ formatStudiengangName(item) }}
            </template>

            <template #item.fachbereich="{ item }">
                {{ getFormattedFachbereich(item.fachbereich) }}
            </template>

            <template #item.studienform="{ item }">
                {{ formatStudienformen(item.studienformen.studienformen) }}
            </template>

            <template #item.standort="{ item }">
                {{ item.standort }}
            </template>

            <template #item.actions="{ item }">
                <v-btn
                    icon="mdi-pencil"
                    class=""
                    density="comfortable"
                    color="primary"
                    size="small"
                    title="Studiengang editieren"
                    @click="openEditModal(item.studiengangId)"
                />
            </template>
        </v-data-table-server>
        <studiengang-edit-modal
            v-if="isEditModalOpen"
            v-model="isEditModalOpen"
            :studiengang-id="selectedStudiengangId"
        />
        <studiengang-create-modal v-if="isCreateModalOpen" v-model="isCreateModalOpen" />
    </div>
</template>

<script setup>
    'use strict'

    import { ref, computed, onMounted, watch } from 'vue'
    import Error from './../Error'
    import { itemsPerPageOptions, itemsPerPageText, loadingText, noItemsText } from '@/js/utils'
    import { useFachbereichStore, useStudiengangStore } from '@/js/stores'
    import StudiengangEditModal from '@/js/components/Studiengang/StudiengangEditModal.vue'
    import StudiengangCreateModal from '@/js/components/Studiengang/StudiengangCreateModal.vue'

    const itemsPerPage = ref(-1)

    const headers = [
        {
            key: 'name',
            title: 'Name',
            sortable: false
        },
        {
            key: 'abschlussArt',
            title: 'Abschluss',
            sortable: false
        },
        {
            key: 'fachbereich',
            title: 'Fachbereich',
            sortable: false
        },
        {
            key: 'studienform',
            title: 'Studienform',
            sortable: false
        },
        {
            key: 'standort',
            title: 'Standort',
            sortable: false
        },
        {
            key: 'actions',
            title: 'Aktionen',
            sortable: false
        }
    ]

    const fachbereichStore = useFachbereichStore()
    const studiengangStore = useStudiengangStore()
    const selectedStudiengangId = ref(null)
    const isEditModalOpen = ref(false)
    const isCreateModalOpen = ref(false)

    const studiengaenge = computed(() => studiengangStore.studiengaenge)

    const errorMessages = computed(() => {
        return [
            studiengangStore.getError('fetch-studiengaenge')?.message,
            fachbereichStore.getError('fetch-fachbereiche')?.message,
            studiengangStore.getError('create-studiengang')?.message,
            studiengangStore.getError('update-studiengang')?.message
        ].filter((msg) => msg != null)
    })
    const hasError = computed(() => errorMessages.value.length > 0)

    function openEditModal(studiengangId) {
        selectedStudiengangId.value = studiengangId
        isEditModalOpen.value = true
    }

    function openCreateModal() {
        isCreateModalOpen.value = true
    }

    function getFormattedFachbereich(fachbereichIri) {
        const fachbereich = fachbereichStore.getFachbereichByIri(fachbereichIri)
        return `${fachbereich?.kurzName}`
    }

    function formatStudiengangName(studiengang) {
        let label = `${studiengang.kurzName} ${studiengang.name}`
        if (studiengang.studiengangVorgaengerId) {
            const vorgaenger = getStudiengangById(studiengang.studiengangVorgaengerId)
            label += ` (vormals ${vorgaenger.kurzName})`
        }

        return label
    }

    function formatStudienformen(studienformen) {
        return Object.values(studienformen)
            .map((f) => f.name)
            .join(', ')
    }

    watch(isEditModalOpen, (newValue) => {
        if (false === newValue) {
            selectedStudiengangId.value = null
        }
    })

    onMounted(() => {
        fachbereichStore.fetchFachbereiche()
        studiengangStore.fetchStudiengaenge()
        studiengangStore.fetchAvailableStudienformen()
        studiengangStore.fetchAvailableAbschlussArten()
    })
</script>
